/**
 * Orval mutator function with dependency injection
 * This module is decoupled from specific auth implementations
 * Token provider must be registered via initializeAxiosInstance()
 */
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import type { ITokenProvider } from './token-provider.interface';
import { attachOrvalAuthRequestInterceptor } from './orvalAuthInterceptor';

let tokenProvider: ITokenProvider | null = null;
let orvalAxios: ReturnType<typeof axios.create> | null = null;

/**
 * Initialize the axios instance with a token provider
 * Must be called once during application startup
 *
 * @param provider - Token provider implementation that handles auth token retrieval
 * @param baseURL - API base URL (e.g., from import.meta.env.VITE_API_BASE_URL)
 * @example
 * ```typescript
 * import { initializeAxiosInstance } from '@libs/api-rest'
 * import authServices from '@/auth/main/DependencyInjection'
 *
 * // In app initialization (main.tsx or similar)
 * initializeAxiosInstance(
 *   { getToken: () => authServices.tokenService.getToken() },
 *   import.meta.env.VITE_API_BASE_URL
 * )
 * ```
 */
export function initializeAxiosInstance(provider: ITokenProvider, baseURL: string): void {
  if (tokenProvider) {
    console.warn('Axios instance already initialized. Reinitializing with new provider.');
  }

  if (!baseURL || baseURL.trim() === '') {
    throw new Error('Base URL is not defined or empty');
  }

  tokenProvider = provider;

  // Create a separate axios instance for Orval with the correct base URL
  orvalAxios = axios.create({
    baseURL
  });
  attachOrvalAuthRequestInterceptor(orvalAxios, () => tokenProvider);
}

/**
 * Orval mutator function - wraps the axios instance
 * Requires initialization via initializeAxiosInstance() first
 */
export const axiosInstance = <T = unknown, E = unknown>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T, E>> => {
  if (!orvalAxios) {
    throw new Error(
      'Axios instance not initialized. Call initializeAxiosInstance() during app startup. ' +
        'Example: initializeAxiosInstance({ getToken: () => authServices.tokenService.getToken() })'
    );
  }

  return orvalAxios.request<T, AxiosResponse<T, E>>({
    ...config,
    ...options
  });
};

export default axiosInstance;
